import { useEffect, useState, ChangeEvent } from "react";
import GoogleBtn from "signInSignUp/googleBtn";
import { AuthFooter } from "components/design/authFooter";
import "../../../static/styles/components/magicLinkSignin.css";
import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import { emailSchema } from "formValidations/emailSchema";
import { useQuery } from "hooks/useQuery";
import { RightArrow } from "components/icons/RightArrow";
import { useHistory } from "react-router-dom";
import { Divider } from "components/design/Divider";
import { FormikForm } from "components/FormikForm/FormikForm";
import { WebAuthnSignInButton } from "components/webauthn/WebAuthnSignInButton";
import { setGoogleTosConsent } from "utils/googleTosConsent/setGoogleTosConsent";
import { ConditionalLink } from "components/conditionalLink";
import { SIGN_UP } from "constants/magicLink";

const clientId = process.env.PUBLIC_CURRENT_GOOGLE_CLIENT_ID;

type SignInFormValues = {
  email: string;
};

type SignInProps = {
  updatePage: (data: {
    email: string;
    accept_tos: boolean;
    first_name: string;
    last_name: string;
  }) => void | Promise<boolean | undefined>;
  isLoading: boolean;
};

const EMAIL_LABEL = (
  <>
    Work Email <span className="t-text-red">*</span>
  </>
);

export const SignIn = ({ updatePage, isLoading }: SignInProps) => {
  const query = useQuery();
  const [isTermsPrivacySelected, setIsTermsPrivacySelected] = useState(false);
  const [showError, setShowError] = useState(false);
  const couponCode = query.get("couponCode") || query.get("couponcode");
  const history = useHistory();

  const onToSPPSelection = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTermsPrivacySelected(e.target.checked);
  };

  useEffect(() => {
    document.title = "Sign In | Inkle";
    if (couponCode) localStorage.setItem("couponCode", couponCode);
  }, [couponCode]);

  const sendMagicLink = ({ email }: SignInFormValues) => {
    if (isTermsPrivacySelected) {
      updatePage({
        email,
        accept_tos: isTermsPrivacySelected,
        first_name: "",
        last_name: "",
      });
    } else {
      setShowError(true);
    }
  };

  const getAccessToken = () => {
    setGoogleTosConsent(isTermsPrivacySelected);
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${window.location.href}&prompt=consent&response_type=token&client_id=${clientId}&scope=openid%20email%20profile&flowName=GeneralOAuthFlow`;
  };

  return (
    <div className="t-flex t-flex-col t-gap-8">
      <div className="t-flex t-flex-col t-gap-1.5">
        <div className="t-text-title-h1 t-font-normal t-text-text-100 sm:t-text-title-h2">
          Sign in
        </div>
        <div className="t-text-body t-text-neutral-50 sm:t-text-body-sm-lite">
          Sign in to your Inkle account to continue.
        </div>
      </div>
      <div className="t-flex t-flex-col t-gap-6">
        <FormikForm<SignInFormValues>
          initialValues={{
            email: "",
          }}
          onSubmit={sendMagicLink}
          validationSchema={emailSchema}
        >
          <div className="t-flex t-flex-col t-gap-8">
            <div className="t-flex t-flex-col t-gap-3">
              <Input
                withForm
                name="email"
                type="email"
                customSize="regular"
                placeholder="you@company.com"
                label={EMAIL_LABEL}
              />
              <AuthFooter
                onChange={onToSPPSelection}
                isChecked={isTermsPrivacySelected}
                showError={!isTermsPrivacySelected && showError}
              />
            </div>
            <Button
              block
              size="regular"
              customType="primary"
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
              rightIcon={<RightArrow />}
            >
              Sign in with magic link
            </Button>
            <div className="-t-mt-5 t-hidden t-items-center t-justify-center t-gap-1.5 t-text-body-sm t-text-text-30 sm:t-flex">
              Don't have an account?
              <ConditionalLink to={SIGN_UP} target="_self">
                Sign up
              </ConditionalLink>
            </div>
          </div>
        </FormikForm>

        <div className="t-flex t-items-center t-justify-between t-gap-2 sm:t-hidden">
          <Divider classNames="t-w-full" />
          <span className="t-text-body t-text-text-30">or</span>
          <Divider classNames="t-w-full" />
        </div>
        <div className="t-grid t-w-full t-grid-cols-2 t-gap-3 sm:t-grid-cols-1">
          <GoogleBtn onClick={getAccessToken} />
          <WebAuthnSignInButton acceptTos={isTermsPrivacySelected} />
        </div>
      </div>
    </div>
  );
};
